/*
 * Filters above the activity feed, plus the subscribe / refresh buttons the
 * tab places in its section header. Filters are compact pill dropdown chips:
 * a search input, checkbox menus for Type / Action / Actor, a date preset and
 * a Command picker for the case.
 */
import {
  Bell,
  BellRing,
  Check,
  ChevronDown,
  Eye,
  EyeOff,
  Filter,
  RefreshCw,
  X
} from "lucide-react";
import {
  forwardRef,
  useMemo,
  useState,
  type ComponentProps,
  type FC
} from "react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList
} from "@/components/ui/command";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from "@/components/ui/dropdown-menu";
import { ListSearch } from "@/components/ui/list-toolbar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from "@/components/ui/popover";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";

import {
  ACTION_KIND_OPTIONS,
  ACTOR_KIND_OPTIONS,
  DATE_RANGE_OPTIONS,
  DEFAULT_ACTIVITY_FILTERS,
  ENTITY_TYPE_OPTIONS,
  activeActivityFilterCount,
  type ActivityCaseOption,
  type ActivityDateRange,
  type ActivityFilters
} from "./activityModel";

// Subscribe -------------------------------------------------------------------

export type SubscribeButtonProps = {
  subscribed: boolean;
  subscriberCount?: number;
  isLoading?: boolean;
  onToggle: () => void;
};

export const SubscribeButton: FC<SubscribeButtonProps> = ({
  subscribed,
  subscriberCount,
  isLoading,
  onToggle
}) => (
  <Tooltip>
    <TooltipTrigger asChild>
      <Button
        variant="ghost"
        size="xs"
        onClick={onToggle}
        disabled={isLoading}
        aria-pressed={subscribed}
        data-testid="subscribe-button"
      >
        {subscribed ? <BellRing /> : <Bell />}
        {subscribed ? "Subscribed" : "Subscribe"}
        {typeof subscriberCount === "number" && subscriberCount > 0 ? (
          <Badge variant="secondary" className="ml-0.5 h-4 min-w-4 px-1">
            {subscriberCount}
          </Badge>
        ) : null}
      </Button>
    </TooltipTrigger>
    <TooltipContent side="bottom">
      {subscribed
        ? "You get notified about activity on this client. Click to stop."
        : "Get notified when anyone changes this client or their cases."}
    </TooltipContent>
  </Tooltip>
);

export const RefreshActivityButton: FC<{
  onRefresh: () => void;
  isRefreshing: boolean;
}> = ({ onRefresh, isRefreshing }) => (
  <Tooltip>
    <TooltipTrigger asChild>
      <Button
        variant="ghost"
        size="icon-xs"
        onClick={onRefresh}
        disabled={isRefreshing}
        aria-label="Refresh activity"
      >
        <RefreshCw className={cn(isRefreshing && "animate-spin")} />
      </Button>
    </TooltipTrigger>
    <TooltipContent>Refresh</TooltipContent>
  </Tooltip>
);

// Pills -----------------------------------------------------------------------

/** A compact pill dropdown chip; filled while it narrows the feed. */
const PillTrigger = forwardRef<
  HTMLButtonElement,
  ComponentProps<typeof Button> & { active: boolean }
>(function PillTrigger({ active, className, children, ...props }, ref) {
  return (
    <Button
      ref={ref}
      variant="pill"
      size="xs"
      className={cn(active && "bg-fill-2 text-fg", className)}
      {...props}
    >
      {children}
      <ChevronDown className="text-fg-4" />
    </Button>
  );
});

type CheckboxPillProps<K extends string> = {
  label: string;
  options: Array<{ key: K; label: string }>;
  selected: Record<K, boolean>;
  onToggle: (key: K) => void;
  onOnly: (key: K) => void;
  onAll: () => void;
};

function CheckboxPill<K extends string>({
  label,
  options,
  selected,
  onToggle,
  onOnly,
  onAll
}: CheckboxPillProps<K>) {
  const on = options.filter((o) => selected[o.key]);
  const active = on.length !== options.length;
  const summary = !active
    ? label
    : on.length === 0
    ? `${label}: none`
    : on.length === 1
    ? on[0].label
    : `${label}: ${on.length}`;
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <PillTrigger active={active} aria-label={`${label} filter`}>
          {summary}
        </PillTrigger>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-56">
        <DropdownMenuLabel className="flex items-center justify-between">
          {label}
          <Button
            variant="ghost"
            size="xs"
            className="h-5 px-1.5 text-xs"
            onClick={(e) => {
              e.preventDefault();
              onAll();
            }}
            disabled={!active}
          >
            All
          </Button>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {options.map((o) => (
          <DropdownMenuCheckboxItem
            key={o.key}
            checked={selected[o.key]}
            onCheckedChange={() => onToggle(o.key)}
            onSelect={(e) => e.preventDefault()}
            className="group/item"
          >
            <span className="flex-1">{o.label}</span>
            <button
              type="button"
              className="invisible ml-2 text-xs text-muted-foreground hover:text-foreground group-hover/item:visible"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onOnly(o.key);
              }}
            >
              only
            </button>
          </DropdownMenuCheckboxItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

const CasePill: FC<{
  cases: ActivityCaseOption[];
  value: string | null;
  onChange: (id: string | null) => void;
}> = ({ cases, value, onChange }) => {
  const [open, setOpen] = useState(false);
  const current = cases.find((c) => c.id === value);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <PillTrigger
          active={Boolean(value)}
          role="combobox"
          aria-expanded={open}
          aria-label="Case filter"
        >
          {current ? current.label : "All Cases"}
        </PillTrigger>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-64 p-0">
        <Command>
          <CommandInput placeholder="Search cases..." />
          <CommandList>
            <CommandEmpty>No cases.</CommandEmpty>
            <CommandGroup>
              <CommandItem
                value="__all__"
                onSelect={() => {
                  onChange(null);
                  setOpen(false);
                }}
              >
                <Check className={cn(value ? "opacity-0" : "opacity-100")} />
                All Cases
              </CommandItem>
              {cases.map((c) => (
                <CommandItem
                  key={c.id}
                  value={`${c.label} ${c.id}`}
                  onSelect={() => {
                    onChange(c.id === value ? null : c.id);
                    setOpen(false);
                  }}
                >
                  <Check
                    className={cn(c.id === value ? "opacity-100" : "opacity-0")}
                  />
                  <span className="truncate">{c.label}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

// Active-filter chips ------------------------------------------------------------

const Chip: FC<{ label: string; onRemove: () => void }> = ({
  label,
  onRemove
}) => (
  <Badge variant="secondary" className="gap-1 pr-1">
    {label}
    <button
      type="button"
      onClick={onRemove}
      aria-label={`Remove ${label}`}
      className="rounded-sm hover:bg-fill-2"
    >
      <X className="size-3" />
    </button>
  </Badge>
);

// Header ----------------------------------------------------------------------

export type ActivityHeaderProps = {
  filters: ActivityFilters;
  onChange: (next: ActivityFilters) => void;
  cases: ActivityCaseOption[];
  /** Loaded events that `filters.routine` hides or shows. */
  routineCount?: number;
};

export const ActivityHeader: FC<ActivityHeaderProps> = ({
  filters,
  onChange,
  cases,
  routineCount = 0
}) => {
  const activeCount = activeActivityFilterCount(filters);

  const toggleIn =
    <G extends "entities" | "actions" | "actors">(group: G) =>
    (key: keyof ActivityFilters[G]) =>
      onChange({
        ...filters,
        [group]: { ...filters[group], [key]: !filters[group][key] }
      });
  const onlyIn =
    <G extends "entities" | "actions" | "actors">(group: G) =>
    (key: keyof ActivityFilters[G]) =>
      onChange({
        ...filters,
        [group]: Object.fromEntries(
          Object.keys(filters[group]).map((k) => [k, k === key])
        )
      });
  const allIn = (group: "entities" | "actions" | "actors") => () =>
    onChange({ ...filters, [group]: DEFAULT_ACTIVITY_FILTERS[group] });

  const chips = useMemo(() => {
    const out: Array<{ key: string; label: string; onRemove: () => void }> = [];
    if (filters.dateRange !== "ALL")
      out.push({
        key: "date",
        label:
          DATE_RANGE_OPTIONS.find((o) => o.key === filters.dateRange)?.label ??
          "Date",
        onRemove: () => onChange({ ...filters, dateRange: "ALL" })
      });
    if (filters.caseId) {
      const c = cases.find((x) => x.id === filters.caseId);
      out.push({
        key: "case",
        label: c ? `Case: ${c.label}` : "Case",
        onRemove: () => onChange({ ...filters, caseId: null })
      });
    }
    ENTITY_TYPE_OPTIONS.filter((o) => !filters.entities[o.key]).forEach((o) =>
      out.push({
        key: `entity:${o.key}`,
        label: `Hide ${o.label.toLowerCase()}`,
        onRemove: () =>
          onChange({
            ...filters,
            entities: { ...filters.entities, [o.key]: true }
          })
      })
    );
    ACTION_KIND_OPTIONS.filter((o) => !filters.actions[o.key]).forEach((o) =>
      out.push({
        key: `action:${o.key}`,
        label: `Hide ${o.label.toLowerCase()}`,
        onRemove: () =>
          onChange({
            ...filters,
            actions: { ...filters.actions, [o.key]: true }
          })
      })
    );
    ACTOR_KIND_OPTIONS.filter((o) => !filters.actors[o.key]).forEach((o) =>
      out.push({
        key: `actor:${o.key}`,
        label: `Hide ${o.label.toLowerCase()}`,
        onRemove: () =>
          onChange({
            ...filters,
            actors: { ...filters.actors, [o.key]: true }
          })
      })
    );
    return out;
  }, [cases, filters, onChange]);

  return (
    <div className="flex shrink-0 flex-col gap-2 px-4 pt-2 pb-1">
      <div className="flex flex-wrap items-center gap-1.5">
        <ListSearch
          className="mr-1 w-48"
          value={filters.query}
          onValueChange={(query) => onChange({ ...filters, query })}
          placeholder="Search activity..."
          aria-label="Search activity"
        />
        <CheckboxPill
          label="Type"
          options={ENTITY_TYPE_OPTIONS}
          selected={filters.entities}
          onToggle={toggleIn("entities")}
          onOnly={onlyIn("entities")}
          onAll={allIn("entities")}
        />
        <CheckboxPill
          label="Action"
          options={ACTION_KIND_OPTIONS}
          selected={filters.actions}
          onToggle={toggleIn("actions")}
          onOnly={onlyIn("actions")}
          onAll={allIn("actions")}
        />
        <CheckboxPill
          label="Actor"
          options={ACTOR_KIND_OPTIONS}
          selected={filters.actors}
          onToggle={toggleIn("actors")}
          onOnly={onlyIn("actors")}
          onAll={allIn("actors")}
        />
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <PillTrigger
              active={filters.dateRange !== "ALL"}
              aria-label="Date range"
            >
              {DATE_RANGE_OPTIONS.find((o) => o.key === filters.dateRange)
                ?.label ?? "All Time"}
            </PillTrigger>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="w-44">
            <DropdownMenuRadioGroup
              value={filters.dateRange}
              onValueChange={(v) =>
                onChange({ ...filters, dateRange: v as ActivityDateRange })
              }
            >
              {DATE_RANGE_OPTIONS.map((o) => (
                <DropdownMenuRadioItem key={o.key} value={o.key}>
                  {o.label}
                </DropdownMenuRadioItem>
              ))}
            </DropdownMenuRadioGroup>
          </DropdownMenuContent>
        </DropdownMenu>
        {cases.length > 0 ? (
          <CasePill
            cases={cases}
            value={filters.caseId}
            onChange={(caseId) => onChange({ ...filters, caseId })}
          />
        ) : null}
        {routineCount > 0 || filters.routine ? (
          <Button
            variant="ghost"
            size="xs"
            className="ml-auto text-fg-3"
            aria-pressed={filters.routine}
            onClick={() => onChange({ ...filters, routine: !filters.routine })}
            data-testid="activity-routine-toggle"
          >
            {filters.routine ? <EyeOff /> : <Eye />}
            {filters.routine
              ? "Hide Routine"
              : `Show Routine (${routineCount})`}
          </Button>
        ) : null}
      </div>

      {chips.length > 0 ? (
        <div className="flex flex-wrap items-center gap-1.5">
          <Filter className="size-3.5 text-muted-foreground" aria-hidden />
          {chips.map((c) => (
            <Chip key={c.key} label={c.label} onRemove={c.onRemove} />
          ))}
          <Button
            variant="ghost"
            size="xs"
            className="text-muted-foreground"
            onClick={() =>
              onChange({ ...DEFAULT_ACTIVITY_FILTERS, query: filters.query })
            }
            disabled={activeCount === 0}
          >
            Clear
          </Button>
        </div>
      ) : null}
    </div>
  );
};
